import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AutocompleteWithList from '@shared/molecules/AutocompleteWithList';
import Drawer from '@shared/atoms/Drawer';
import FormInput from '@shared/atoms/FormFields/FormInput';
import FormToggle from '@shared/atoms/FormFields/FormToggle';
import HorizontalRule from '@shared/atoms/HorizontalRule';
import T from '@shared/atoms/Typography';
import LanguageSelect from '@shared/molecules/LanguageSelect';
import ListItems from '@shared/molecules/ListItems';
import Selector from '@shared/atoms/Selector';

import { newSelectTranslation, useForm } from '@shared/utils';
import theme from '@shared/themes/default';
import { AUTOCOMPLETEWITHLIST_TYPE, DOMAIN_LOCALES } from '@shared/const';

import { useAppGlobals } from '../../../contexts/AppContext';
import { listCatalogs } from '../graphql/queries';

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  margin: 0 ${theme.spacing.m1} ${theme.spacing.m1} ${theme.spacing.m1};
  width: 496px;
`;

const FormRow = styled.div`
  align-items: end;
  display: flex;
  gap: ${theme.spacing.s};
`;

const ToggleRow = styled.div`
  display: flex;
  gap: ${theme.spacing.s};
`;

const SelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
  margin-top: ${theme.spacing.s};
`;

const CatalogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
  margin-left: ${theme.spacing.m3};
  width: 418px;
`;

const CoursesWrapper = styled.div`
  margin-top: ${theme.spacing.m3};
`;

const CategoryDrawer = ({
  category,
  handleClose,
  handleCreate,
  handleCreateEnded,
  handleUpdate,
  handleUpdateEnded,
  open,
  showError,
}) => {
  const { domain, locale, useLazyGraphQLQuery } = useAppGlobals();
  const { t } = useTranslation();
  const locales = DOMAIN_LOCALES[domain];

  const [drawerLocale, setDrawerLocale] = useState(locale);
  const [everyone, setEveryone] = useState(true);
  const [selectedCatalogs, setSelectedCatalogs] = useState([]);

  const [listCatalogsQuery, listCatalogsResponse] =
    useLazyGraphQLQuery(listCatalogs);

  const {
    disableSubmit,
    edited,
    errors: formErrors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    reset,
    submitting,
    setDefaults,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      title: {
        localised: true,
        maxLength: 70,
        required: true,
      },
      description: {
        localised: true,
        maxLength: 250,
      },
      ordinal: {
        mode: 'numeric',
        pattern: '[0-9]*',
        required: true,
        validation: (value) => /^[0-9]*$/.test(value),
      },
      expand: {},
      catalogs: {},
    },
    locales,
    onSubmitHook: (newCategory) => {
      const newCategoryInput = {
        name: newCategory.title,
        description: newCategory.description,
        ordinal: parseInt(newCategory.ordinal, 10),
        expand: newCategory.expand,
        catalogs: newCategory.catalogs?.length ? newCategory.catalogs : [],
      };
      if (category.id) {
        const languages = {};
        languages.updateLanguages = newCategoryInput.name.map(
          ({ languageCode }) => languageCode
        );
        const deleteLanguages = locales.reduce((acc, curr) => {
          if (!languages.updateLanguages.includes(curr)) {
            acc.push(curr);
          }
          return acc;
        }, []);
        if (deleteLanguages.length) {
          languages.deleteLanguages = deleteLanguages;
        }

        handleUpdate({
          ...newCategoryInput,
          id: category.id,
          languages,
        });
      } else {
        handleCreate({
          ...newCategoryInput,
          languages: newCategoryInput.name.map(
            ({ languageCode }) => languageCode
          ),
        });
      }
    },
    onSubmitHookEnded: category?.id ? handleUpdateEnded : handleCreateEnded,
    requiredLocales: [locale],
  });

  useEffect(() => {
    if (category) {
      setDefaults({
        title: category?.name || [],
        description: category?.description || [],
        ordinal: category.ordinal?.toString() || '',
        expand: !!category.expand,
        catalogs: category.catalogs?.map((catalog) => catalog.id) || [],
      });
      setEveryone(!category.id || category.catalogs?.length === 0);
      setSelectedCatalogs(
        category.id && category.catalogs?.length
          ? category.catalogs?.map((catalog) => ({
              key: catalog.id,
              title: newSelectTranslation(catalog.name),
            }))
          : []
      );
    } else {
      reset();
    }
  }, [category]);

  // Reset language when the drawer is reopened
  useEffect(() => {
    setDrawerLocale(locale);
  }, [open]);

  useEffect(() => {
    if (everyone) {
      setSelectedCatalogs([]);
      handleChange({ name: 'catalogs', value: [] });
    }
    if (!everyone && !listCatalogsResponse.data?.listCatalogs) {
      listCatalogsQuery({
        variables: {
          pagination: { limit: 1000 },
          filter: { excludeVideocation: true },
        },
      });
    }
  }, [everyone]);

  return (
    <Drawer
      anchor="right"
      edited={edited}
      errorMessage={
        showError ? t('backoffice.categories.errorSaving') : undefined
      }
      open={open}
      onClose={handleClose}
      onSave={handleSubmit}
      title={
        category?.id
          ? newSelectTranslation(category.name, locale)
          : t('backoffice.categories.new')
      }
      saveDisabled={disableSubmit || submitting}
      saving={submitting}
    >
      <DrawerContent>
        <FormRow>
          <LanguageSelect
            languages={locales}
            onChange={({ value }) => setDrawerLocale(value)}
            value={drawerLocale}
          />
          <FormInput
            id="title"
            label={t('backoffice.categories.title')}
            locale={drawerLocale}
            name="title"
            placeholder={t('backoffice.categories.titlePlaceholder')}
            showMaxLength
            theme="dark"
            errors={formErrors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            values={values}
          />
        </FormRow>
        <FormInput
          id="description"
          label={t('backoffice.categories.description')}
          locale={drawerLocale}
          name="description"
          minRows={6}
          multiline
          placeholder={t('backoffice.categories.descriptionPlaceholder')}
          showMaxLength
          errors={formErrors}
          handleBlur={handleBlur}
          handleChange={handleChange}
          properties={properties}
          values={values}
        />
        <FormInput
          id="ordinal"
          label={t('backoffice.categories.ordinal')}
          name="ordinal"
          placeholder={t('backoffice.categories.ordinalPlaceholder')}
          errors={formErrors}
          handleBlur={handleBlur}
          handleChange={handleChange}
          properties={properties}
          sx={{ width: '20%' }}
          values={values}
        />
        <ToggleRow>
          <FormToggle
            id="expand"
            label={t('backoffice.categories.expand')}
            name="expand"
            handleChange={handleChange}
            properties={properties}
            values={values}
          />
        </ToggleRow>
        <SelectorWrapper>
          <T fontWeight="semibold">{`${t('backoffice.categories.availability')}:`}</T>
          <div>
            <Selector
              onChange={(value) => setEveryone(value === 'everyone')}
              options={[
                { value: 'Everyone', key: 'everyone' },
                { value: 'Catalog', key: 'catalog' },
              ]}
              value={everyone ? 'everyone' : 'catalog'}
            />
          </div>
        </SelectorWrapper>
        {!everyone && (
          <CatalogWrapper>
            <T fontWeight="semibold">{`${t(
              'backoffice.categories.selectCatalogs'
            )}:`}</T>
            <div>
              <AutocompleteWithList
                disabled={listCatalogsResponse.loading}
                items={
                  listCatalogsResponse.data?.listCatalogs
                    ? [
                        ...listCatalogsResponse.data.listCatalogs.reduce(
                          (acc, curr) => {
                            if (
                              !selectedCatalogs?.some(
                                (catalog) => catalog.key === curr.id
                              )
                            ) {
                              acc.push({
                                key: curr.id,
                                title: newSelectTranslation(curr.name),
                              });
                            }
                            return acc;
                          },
                          []
                        ),
                      ]
                    : []
                }
                loading={listCatalogsResponse.loading}
                loadingText={t('generic.loadingText')}
                noOptionsText={t('generic.noSearchResults')}
                onChange={(selectedItems) => {
                  setSelectedCatalogs(selectedItems);
                  handleChange({
                    name: 'catalogs',
                    value: selectedItems?.map((catalog) => catalog.key),
                  });
                }}
                selectedItems={selectedCatalogs}
                theme="dark"
                type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
              />
            </div>
          </CatalogWrapper>
        )}
        {category?.courses && (
          <CoursesWrapper>
            <HorizontalRule />
            <T fontWeight="bold">
              {t('backoffice.categories.courses', {
                count: category.courses.length,
              })}
            </T>
            {category?.courses.length > 0 && (
              <ListItems
                items={category.courses.map((course) => ({
                  key: course.id,
                  title: newSelectTranslation(course.name, locale),
                }))}
                theme="dark"
              />
            )}
          </CoursesWrapper>
        )}
      </DrawerContent>
    </Drawer>
  );
};

CategoryDrawer.propTypes = {
  category: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  handleCreate: PropTypes.func.isRequired,
  handleCreateEnded: PropTypes.bool,
  handleUpdate: PropTypes.func.isRequired,
  handleUpdateEnded: PropTypes.bool,
  open: PropTypes.bool,
  showError: PropTypes.bool,
};

export default CategoryDrawer;
